import React, { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useLocation, useNavigate } from 'react-router-dom';
import HomeDashboardLayout from '../HomeDashboardLayout';
import HeaderProfileSection from '../HeaderProfileSection';
import DoubtTabs from './DoubtTabs';
import AllDoubts from './AllDoubts';
import DoubtFilter from './DoubtFilter';
import { flattenDoubtPages, useDoubtFilters, useDoubtListActions, useDoubtReportReasons, useDoubtsList } from '../../hooks/useDoubts';
import { buildDoubtFlowState, persistDoubtFlowState } from './doubtFlowState';
import { DOUBT_ROUTES } from './doubtRoutes';
import { getStoredUser } from '../../utils/authStorage';
import '../../dashboard/Dashboard.css';
import '../HomeDashboard.css';
import '../questionBank/QuestionBankList.css';
import './Doubt.css';
import cameraIcon from '../../assets/images/bagIcon.png';

const tabFilterTypes = { all: 'all', 'your-doubts': 'your_doubts', 'your-answer': 'your_answers' };

const Doubts = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const storedUser = getStoredUser();
  const flow = useMemo(() => buildDoubtFlowState(location.state), [location.state]);
  const [activeTab, setActiveTab] = useState('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({ subjectIds: [], topicIds: [], tagCategoryIds: [] });
  const { ref: loadMoreRef, inView } = useInView({ rootMargin: '360px 0px' });
  const categoryId = Number(flow?.category_id || 2);
  const stateId = Number(flow?.state_id || 0);
  const doubtId = Number(flow?.doubt_id || flow?.selectedDoubt?.id || 0);
  const language = String(flow?.language || storedUser?.content_language || storedUser?.language || 'EN').toUpperCase();
  const isListEntry = !doubtId;
  const doubtsQuery = useDoubtsList({ filterType: tabFilterTypes[activeTab], ...filters, language, enabled: isListEntry });
  const listActions = useDoubtListActions();
  const filtersQuery = useDoubtFilters(categoryId, language, isListEntry);
  useDoubtReportReasons(language, isListEntry);
  const doubts = useMemo(() => flattenDoubtPages(doubtsQuery.data), [doubtsQuery.data]);

  useEffect(() => {
    if (inView && doubtsQuery.hasNextPage && !doubtsQuery.isFetchingNextPage) {
      doubtsQuery.fetchNextPage();
    }
  }, [doubtsQuery, inView]);

  const changeTab = (tab) => {
    setActiveTab(tab);
    setFilters({ subjectIds: [], topicIds: [], tagCategoryIds: [] });
  };

  const openDoubt = (doubt) => {
    const detailState = buildDoubtFlowState({
      ...flow,
      selectedDoubt: doubt,
      doubt_id: doubt.id,
      qbankBackTarget: { pathname: DOUBT_ROUTES.list, state: flow },
    });
    persistDoubtFlowState(detailState);
    navigate(DOUBT_ROUTES.detail, { state: detailState });
  };

  const shareDoubt = async (doubt) => {
    const shareUrl = `${window.location.origin}${DOUBT_ROUTES.detail}?doubt_id=${encodeURIComponent(doubt.id)}&state_id=${encodeURIComponent(stateId)}&category_id=${encodeURIComponent(categoryId)}`;
    const shareData = { title: doubt.title || 'Doubt', text: 'View this doubt on Vidyakasam Education.', url: shareUrl };
    try {
      if (navigator.share) await navigator.share(shareData);
      else if (navigator.clipboard) await navigator.clipboard.writeText(shareUrl);
    } catch (error) {
      if (error?.name !== 'AbortError') console.error('Unable to share doubt.', error);
    }
  };

  return <><style>{`.homeDashboard-wrapper .page-content { padding-right: 0; }`}</style><HomeDashboardLayout showHeader={false}>
    <div className="qbl-page qbankList-page"><div className="start-quiz qbl-main qbl-panel"><div className="hideProfile-page"><div className="categories-page-header mb-0"><div><button type="button" className="categories-back-button" onClick={() => window.history.back()} aria-label="Go back"><i className="bi bi-chevron-left" /></button></div><div><h4 className="d-flex d-sm-none categories-page-title"><span className="visually-hidden">Doubts</span></h4></div><HeaderProfileSection /></div></div>
      <div className="top-margin"><h1 className="qb-title">Doubts</h1><p className="qb-sub">Practice more, score higher</p><div className="ask-doubts-wrapper"><button type="button" className="ask-doubts-input" onClick={() => navigate(DOUBT_ROUTES.ask, { state: flow })}><span className="ask-doubts-placeholder">Ask Your Doubts</span><span className="ask-doubts-camera"><img src={cameraIcon} alt="" /></span></button><button type="button" className="ask-doubts-filter quizz-Filter-icon d-flex d-lg-flex d-xl-none" onClick={() => setIsFilterOpen(true)} aria-label="Open filters" aria-expanded={isFilterOpen} aria-controls="doubt-filter-drawer"><span className="quizz-Filter"><i className="bi bi-sliders2" /></span></button></div></div>
      <div className="doubts-page">        <DoubtTabs activeTab={activeTab} setActiveTab={changeTab} />
        {doubtsQuery.isError ? (
          <div className="doubts-empty-state doubts-empty-state--error">
            {doubtsQuery.error?.message || 'Unable to load doubts.'}
          </div>
        ) : (
          <AllDoubts
            doubts={doubts}
            isLoading={doubtsQuery.isLoading}
            isFetchingNextPage={doubtsQuery.isFetchingNextPage}
            hasNextPage={doubtsQuery.hasNextPage}
            loadMoreRef={loadMoreRef}
            onOpen={openDoubt}
            onShare={shareDoubt}
            onUpvote={(item) => listActions.upvote.mutate({ doubtId: item.id })}
            onBookmark={(item) => listActions.bookmark.mutate({ doubtId: item.id })}
            isUpvotePending={listActions.upvote.isPending}
            isBookmarkPending={listActions.bookmark.isPending}
            emptyMessage={
              filters.subjectIds.length || filters.topicIds.length || filters.tagCategoryIds.length
                ? 'No doubts match the selected filters.'
                : 'No doubts found.'
            }
            language={language}
          />
        )}
      </div>
    </div>
    <div className={`qbl-filterBackdrop${isFilterOpen ? ' is-open' : ''}`} onClick={() => setIsFilterOpen(false)} aria-hidden="true" />
    <div id="doubt-filter-drawer" className={`qbl-filterPanel${isFilterOpen ? ' is-open' : ''}`}>
      <DoubtFilter className={isFilterOpen ? 'is-open' : ''} onClose={() => setIsFilterOpen(false)} onApply={setFilters} subjects={filtersQuery.data?.subjects || []} tags={filtersQuery.data?.tags || []} appliedFilters={filters} isLoading={filtersQuery.isLoading} />
    </div>
  </div></HomeDashboardLayout></>;
};

export default Doubts;
